// @ts-strict-ignore
import {
  AfterViewChecked,
  AfterViewInit,
  ChangeDetectorRef,
  Directive, EventEmitter, Input, OnDestroy,
  OnInit, Output, QueryList,
  ViewChild,
  ViewChildren
} from '@angular/core';
import {
  LMListEventService,
  LMListGridPagingService,
  LMBlockItemComponent,
  LMListSpecUtilService,
  LMListUtilService, LMBlockGridComponent
} from '@app/components';
import { SohoBusyIndicatorDirective } from 'ids-enterprise-ng';
import { Observable, of, Subject } from 'rxjs';
import {
  LMBusyIndicatorService,
  LMContextService,
  LMErrorUtilService, LMFormatUtilService,
  LMLoggerService,
  LMUrlUtilService
} from '@app/services';
import { LMThemeService } from '@banner/lm-theme.service';
import { AppConstants, LMConditionUtilService } from '@app/utils';
import * as urltemplate from 'url-template';
import { LMListContentBaseComponent } from './lm-list-content-base.component';

export interface PagerIdAttributes {
  name?: string;
  value?: string;
}

@Directive()
export abstract class LMListBlockGridBaseDirective extends LMListContentBaseComponent implements OnInit, AfterViewInit, AfterViewChecked, OnDestroy, LMListContentInterface {
  public hasNextPage = false;
  public hasPreviousPage = false;
  public hasFirstPage = false;
  public hasLastPage = false;
  public pageSize = 20;
  public firstPageButtonTooltip;
  public previousPageButtonTooltip;
  public nextPageButtonTooltip;
  public lastPageButtonTooltip;
  public showPageSizeSelector = false;
  public pagerIdAttributes: Array<PagerIdAttributes>;
  public blockGridOptions: BlockGridOptions = undefined;
  public menuIsOpen = false;
  public _showEmptyMessage = false;
  public recordCount = 0;
  public showAsCard: boolean;
  public selectionMode: SohoListViewSelectable;
  public showAsMingleWidget = false;
  public testVersion1: boolean;
  public uiLoadingIndicatorTimeout = 0;
  public initialBuildComplete = false;

  private selectedItem: LMBlockItemComponent;
  private implementsCancelablePaging = false;
  private cancelPagingNotifier$: Subject<string>;
  private fFromDataViewChanged = false;
  private selectedRows: LMListRowData[] = [];
  private m_hasFilterList = false;
  /**
   * View child reference to the soho busy indicator - use for paging.
   */
  @ViewChild(SohoBusyIndicatorDirective, { static: true }) sohoBusyIndicator: SohoBusyIndicatorDirective;
  @ViewChild('LMBlockGridComponent') lmBlockGridComponent: LMBlockGridComponent;
  @ViewChildren('LMBlockItem') lmBlockItems = new QueryList<LMBlockItemComponent>();

  /**
   * Only load data if filterCriteria or filterListFilterCriteria are set.
   */
  @Input() set hasFilterList(hasFilterList: boolean) {
    this.m_hasFilterList = hasFilterList;
  }

  get hasFilterList(): boolean {
    return this.m_hasFilterList;
  }

  @Output() itemSelected = new EventEmitter<LMBlockGridItemEvent>();
  @Output() itemDoubleClicked = new EventEmitter<LMBlockGridItemEvent>();
  @Output() toolbarButtonEvent = new EventEmitter<LMToolbarButtonEvent>();
  @Output() uiLinkEvent = new EventEmitter<LMUILinkEvent>();

  set showEmptyMessage(bool: boolean) {
    this._showEmptyMessage = bool;
  }

  get showEmptyMessage(): boolean {
    return this._showEmptyMessage;
  }

  constructor(
    protected lmContextService: LMContextService,
    protected lmLoggerService: LMLoggerService,
    protected lmFormatUtilService: LMFormatUtilService,
    protected lmConditionUtilService: LMConditionUtilService,
    protected lmThemeService: LMThemeService,
    protected lmBusyIndicatorService: LMBusyIndicatorService,
    protected changeDetectorRef: ChangeDetectorRef,
    protected lmErrorUtilService: LMErrorUtilService,
    protected lmListEventService: LMListEventService,
    protected lmListGridPagingService: LMListGridPagingService,
    protected lmListSpecUtilService: LMListSpecUtilService,
    protected lmListUtilService: LMListUtilService,
    protected lmUrlUtilService: LMUrlUtilService
  ) {
    super(lmLoggerService, lmThemeService, lmFormatUtilService, lmListUtilService, lmConditionUtilService);
    this.implementsCancelablePaging = this.lmContextService.isUiPagingRequestWaitTimeEnabled;
    this.testVersion1 = this.lmContextService.testVersion() === AppConstants.TESTVERSION_V1;
  }

  ngOnInit() {
    this.clearEmptyMessage();
    this.validateInitialInputs();
    this.initialize();

    if (!this.listSpec.dependentList) {
      this.build();
    }
  }

  ngAfterViewInit() {
    // noop
  }

  ngAfterViewChecked() {
    if (this.componentDestroyed) {  // this caused problems when the list had no records
      return;
    }

    if (this.listSpec.dependentList && this.fFromDataViewChanged) {
      this.fFromDataViewChanged = false;
      setTimeout(() => this.build());
    }
  }

  ngOnDestroy() {
    this.setComponentDestroyed();
    this.cancelOutstandingPagingRequest();
  }

  protected loadManualData() {
    this.initialPage();
  }

  protected onFormButtonClicked(columnDef: ListGridColumn, dataView: LMDataView, row: any, cell: any, navigationParams: any, event: any): void {
    // Handle click of the no data message button
    if (columnDef.displayField.linkNavigation ||
      columnDef.displayField.navigation ||
      columnDef.displayField.httpUrl ||
      navigationParams
    ) {
      const uiLinkEvent: LMUILinkEvent = { columnDef, dataView, event, navigationParams, options: { fromNoDataMessageFormButton: true } };
      this.lmListEventService.uiLinkEvent(uiLinkEvent);
    } else if (columnDef.displayField.type === AppConstants.FORMBUTTON ||
      columnDef.displayField.type === AppConstants.SUBFORM ||
      columnDef.displayField.type === AppConstants.FORMREFERENCE ||
      columnDef.displayField.type === AppConstants.LISTREFERENCE) {
      this.lmListEventService.formButtonEvent(<LMFormButtonEvent>{ columnDef, dataView, row, cell, event });
    }
  }

  fromDataViewChanged() {
    // TODO this is different why???
    if (this.listSpec.dependentList && !this.componentDestroyed) {
      this.fFromDataViewChanged = true;
    }
  }

  public isModified(): boolean {
    return false;
  }

  public getDirtyRows(commitEdits: boolean = undefined): Array<LMDataView> {
    return [];
  }

  get currentDataViewSet(): LMDataViewSet {
    return this.dataViewSet;
  }

  // TODO implement me.
  public updateDataView(key: number, dataView: LMDataView) {
    // noop
  }

  public addDataView(dataView: LMDataView, index: number) {
    // noop
  }

  public updateDataViewTransaction(key: number, status: string, message: string) {
    // noop
  }

  public setDirtyIndicator(row: number, cell: number, showDirty: boolean) {
    // noop
  }

  public onContextMenuItemSelected(actionReference: LMBusActionReference, selectedDataView?: LMDataView): void {
    // TODO
  }

  public disableSaveInUserListViewMenu(): boolean {
    // TODO: The saved search depends on filter criteria.
    // TODO: Enable or disable based on sort order that is part of LMSearchState.
    if (!this.listState.filterCriteria?.length)
      return true;
    else if (this.listState.filterCriteria.some(fc => fc.isAdHoc === true))
      return true;
    else
      return false; //Save is enabled when at least 1 filter criteria exists and none of them are ad hoc 
  }

  public removeDataView(index: number) {
    // noop
  }

  public getDataViewIndex(event: ChartEvent): number {
    return event.data.ref;
  }

  public onUILinkEvent(uiLinkEvent: LMUILinkEvent) {
    this.lmListEventService.uiLinkEvent(uiLinkEvent);
  }

  private reset() {
    // set dataViewSet
    // this.dataViewSet = {};

    this.recordCount = 0;

    // set pager button state
    this.hasNextPage = false;
    this.hasPreviousPage = false;
    this.hasFirstPage = false;
    this.hasLastPage = false;
    this.showPageSizeSelector = false;

    // build the image list
    // this.buildImageListData();
  }

  private handleInitialPageCancel() {
    this.reset();
    this.showEmptyMessage = true;
    const message = '';
    const title = Soho.Locale.translate('NoDataAvailable');
    this.handleError(message, title);  // THIS IS NOT SHOWING AN ERROR
    this.changeDetectorRef.markForCheck();
  }

  private handleCancelableRequest(): void {
    if (this.implementsCancelablePaging && this.cancelPagingNotifier$) {
      this.cancelPagingNotifier$.next(AppConstants.THREAD_INTERRUPTED_BY_WEBUI);
      this.cancelPagingNotifier$.complete();
      this.cancelPagingNotifier$ = null;
    }
  }

  private handleCancelableResponse(response: LMListPagingResponse): boolean {
    if (this.implementsCancelablePaging) {
      if (response.status === 'STARTED') {
        this.lmListEventService.cancelablePagingEvent(<LMCancelablePagingEvent>{ state: 'started', cancelablePagingNotifier: response.cancelablePagingNotifier });
        this.cancelPagingNotifier$ = response.cancelablePagingNotifier;
        return true;
      } else if (response.status === 'WAITING') {
        this.lmListEventService.cancelablePagingEvent(<LMCancelablePagingEvent>{ state: 'waiting' });
        return true;
      } else if (response.status === 'CANCELED') {
        this.lmListEventService.cancelablePagingEvent(<LMCancelablePagingEvent>{ state: 'canceled' });
        this.lmBusyIndicatorService.hideBusyIndicator(this.sohoBusyIndicator);
        return true;
      } else {
        this.cancelPagingNotifier$ = null;
        this.lmListEventService.cancelablePagingEvent(<LMCancelablePagingEvent>{ state: 'finished' });
        return false;
      }
    } else {
      return false;
    }
  }

  private initialPageWait(): Observable<boolean> {
    return this._initialPage();
  }

  private initialPage(rebuildInitialPageUrl = false) {
    this.lmBusyIndicatorService.showBusyIndicator(this.sohoBusyIndicator);
    this._initialPage(rebuildInitialPageUrl).subscribe();
  }

  private disableComponent() {
    // set dataViewSet
    this.dataViewSet = undefined;

    // set pager button state
    this.hasNextPage = false;
    this.hasPreviousPage = false;
    this.hasFirstPage = false;
    this.hasLastPage = false;
    this.showPageSizeSelector = false;

    // todo need to remove any selection and any data currently in the block grid
    this.blockGridOptions = undefined;

    this.changeDetectorRef.markForCheck();
  }

  private _initialPage(rebuildInitialPageUrl = false): Observable<boolean> {
    return new Observable<boolean>(observer => {
      // console.log('listimage-initialPage: ' + this.listSpec.busListSpec.name);

      if (this.componentDestroyed) {
        observer.next(true);
        observer.complete();
        return;
      }

      this.showEmptyMessage = false;

      if (this.listSpec.dependentList) {
        if (this.fromDataView === undefined) {
          this.lmBusyIndicatorService.hideBusyIndicator(this.sohoBusyIndicator);
          observer.next(true);
          observer.complete();
          return; // safety
        }

        if (this.fromDataView === null) {
          // disabled flag should be set at this point! That makes disabling
          // the component here seem like we are doing disabled checking in 2 spots.
          // todo: ppatton Look into more consistent (and single) way of disabling list-grid, list-view, etc...
          this.disableComponent();
          this.lmBusyIndicatorService.hideBusyIndicator(this.sohoBusyIndicator);
          /**
           * emitting the dataLoaded event with a null so the lm-list object
           * can responsd appropriately.
           * todo: ppatton again, why do this here?! shouldn't the lm-list already now it's been disabled?!
           */
          setTimeout(() => this.lmListEventService.dataLoadedEvent(null));
          observer.next(true);
          observer.complete();
          return;
        }
      }

      this.handleCancelableRequest();
      this.cancelOutstandingPagingRequest();

      const url = this.buildInitialPageUrl(rebuildInitialPageUrl);
      const postContent = this.lmUrlUtilService.buildSelectDataViewPagingRequest(this.selectRequestData);
      const isManualLoad = this.isManualLoading();
      const initialNbrRecs = this.lmListUtilService.getInitialNumberOfRecs(this.listSpec, this.listState, this.pageSize);

      this.lmLoggerService.time({ id: 'LMListView.initialPage() ' + this.instanceId });
      this.pagingSubscription = this.lmListGridPagingService.initialPageFiltered(url, this.filterCriteria, initialNbrRecs, postContent, isManualLoad).subscribe(
        (listPagingResponse: LMListPagingResponse) => {

          if (this.handleCancelableResponse(listPagingResponse)) {
            if (listPagingResponse.status === 'CANCELED') {
              this.handleInitialPageCancel();
            }
            observer.next(true);
            observer.complete();
            return;
          }

          if (listPagingResponse.serverLog) {
            this.lmLoggerService.serverLog(listPagingResponse.serverLog);
          }

          this.lmLoggerService.timeEnd('LMListView.initialPage() ' + this.instanceId, [ this, listPagingResponse.dataViewSet ]);

          this.setEmptyMessage();

          if (listPagingResponse.status === 'COMPLETED') {
            this.postLoad(listPagingResponse);
            this.lmBusyIndicatorService.hideBusyIndicator(this.sohoBusyIndicator);
            setTimeout(() => this.lmListEventService.dataLoadedEvent(listPagingResponse));
          } else {
            this.handleError(listPagingResponse.details, listPagingResponse.statusText);
          }
          observer.next(listPagingResponse.status === 'COMPLETED');
          observer.complete();
        },
        error => {
          observer.next(false);
          observer.complete();
          this.lmBusyIndicatorService.hideBusyIndicator(this.sohoBusyIndicator);
          this.handleError(error.stack, error.message);
        }
      );
    });
  }

  private reloadPage(): void {
    this.handleCancelableRequest();
    this.cancelOutstandingPagingRequest();
    this.lmBusyIndicatorService.showBusyIndicator(this.sohoBusyIndicator);

    this.pagingSubscription = this.lmListGridPagingService.reloadPage(this.currentDataViewSet, this.pageSize).subscribe(
      (listPagingResponse: LMListPagingResponse) => {

        if (this.handleCancelableResponse(listPagingResponse)) {
          return;
        }

        if (listPagingResponse.serverLog) {
          this.lmLoggerService.serverLog(listPagingResponse.serverLog);
        }

        this.lmBusyIndicatorService.hideBusyIndicator(this.sohoBusyIndicator);

        if (listPagingResponse.status === 'COMPLETED') {
          this.postLoad(listPagingResponse);
        } else {
          this.handleError(listPagingResponse.details, listPagingResponse.statusText);
        }
      },
      (error: any) => {
        this.lmBusyIndicatorService.hideBusyIndicator(this.sohoBusyIndicator);
        this.handleError(error.stack, error.message);
      });
  }

  onFirstPage() {
    this.handleCancelableRequest();
    this.cancelOutstandingPagingRequest();
    this.lmBusyIndicatorService.showBusyIndicator(this.sohoBusyIndicator);
    this.pagingSubscription = this.lmListGridPagingService.firstPage(this.currentDataViewSet, this.pageSize).subscribe(
      (listPagingResponse: LMListPagingResponse) => {

        if (this.handleCancelableResponse(listPagingResponse)) {
          return;
        }

        this.lmBusyIndicatorService.hideBusyIndicator(this.sohoBusyIndicator);

        if (listPagingResponse.status === 'COMPLETED') {
          this.postLoad(listPagingResponse);
          setTimeout(() => this.unSelectAllRows());
        } else {
          this.handleError(listPagingResponse.details, listPagingResponse.statusText);
        }
      },
      error => {
        this.lmBusyIndicatorService.hideBusyIndicator(this.sohoBusyIndicator);
        this.handleError(error.stack, error.message);
      });
  }

  onPreviousPage() {
    this.handleCancelableRequest();
    this.cancelOutstandingPagingRequest();
    this.lmBusyIndicatorService.showBusyIndicator(this.sohoBusyIndicator);
    this.pagingSubscription = this.lmListGridPagingService.previousPage(this.currentDataViewSet).subscribe(
      (listPagingResponse: LMListPagingResponse) => {

        if (this.handleCancelableResponse(listPagingResponse)) {
          return;
        }

        this.lmBusyIndicatorService.hideBusyIndicator(this.sohoBusyIndicator);

        if (listPagingResponse.status === 'COMPLETED') {
          this.postLoad(listPagingResponse);
          setTimeout(() => this.unSelectAllRows());
        } else {
          this.handleError(listPagingResponse.details, listPagingResponse.statusText);
        }
      },
      error => {
        this.lmBusyIndicatorService.hideBusyIndicator(this.sohoBusyIndicator);
        this.handleError(error.stack, error.message);
      });
  }

  onNextPage() {
    this.handleCancelableRequest();
    this.cancelOutstandingPagingRequest();
    this.lmBusyIndicatorService.showBusyIndicator(this.sohoBusyIndicator);
    this.pagingSubscription = this.lmListGridPagingService.nextPage(this.currentDataViewSet).subscribe(
      (listPagingResponse: LMListPagingResponse) => {

        if (this.handleCancelableResponse(listPagingResponse)) {
          return;
        }

        this.lmBusyIndicatorService.hideBusyIndicator(this.sohoBusyIndicator);

        if (listPagingResponse.status === 'COMPLETED') {
          this.postLoad(listPagingResponse);
          setTimeout(() => this.unSelectAllRows());
        } else {
          this.handleError(listPagingResponse.details, listPagingResponse.statusText);
        }
      },
      error => {
        this.lmBusyIndicatorService.hideBusyIndicator(this.sohoBusyIndicator);
        this.handleError(error.stack, error.message);
      });
  }

  onLastPage() {
    this.handleCancelableRequest();
    this.cancelOutstandingPagingRequest();
    this.lmBusyIndicatorService.showBusyIndicator(this.sohoBusyIndicator);

    this.pagingSubscription = this.lmListGridPagingService.lastPage(this.currentDataViewSet).subscribe(
      (listPagingResponse: LMListPagingResponse) => {
        this.lmBusyIndicatorService.hideBusyIndicator(this.sohoBusyIndicator);

        if (listPagingResponse.status === 'CANCELED') {
          return;
        }

        if (listPagingResponse.status === 'COMPLETED') {
          this.postLoad(listPagingResponse);
          setTimeout(() => this.unSelectAllRows());
        } else {
          this.handleError(listPagingResponse.details, listPagingResponse.statusText);
        }
      },
      error => {
        this.lmBusyIndicatorService.hideBusyIndicator(this.sohoBusyIndicator);
        this.handleError(error.stack, error.message);
      });
  }

  refresh(): Observable<boolean> {
    if (this.isBusyPaging()) {
      return of (true);
    }

    this.handleCancelableRequest();
    this.cancelOutstandingPagingRequest();
    // console.log('listimage-refresh: ' + this.listSpec.busListSpec.name);
    return this.initialPageWait();
  }

  onPageSizeChange(args: { pageOp: 'pagesize', pageSize: number }) {
    if (!this.showPageSizeSelector) {
      return; // safety
    }
    this.pageSize = args.pageSize;
    this.listPersonalization.pageSize = args.pageSize;
    this.reloadPage();
  }

  public togglePagingSelectMenu() {
    this.menuIsOpen = !this.menuIsOpen;

    // CHANGE DETECTION
    this.changeDetectorRef.detectChanges();
  }

  public getSelectedRow(): number {
    return this.selectedRows.map((lmListRowData) => lmListRowData.idx)[0];
  }

  public getSelectedRows(): Array<number> {
    return this.selectedRows.map((lmListRowData) => lmListRowData.idx);
  }

  public unSelectAllRows(): void {
    this.selectedItem = null;
    this.lmBlockItems.forEach((blockItem) => {
      if (blockItem?.unCheck) {
        blockItem.unCheck();
      }
    });
    this.selectedRows = [];

    const listRowSelectedEvent: LMListRowSelectedEvent = {
      event: null,
      rows: this.selectedRows
    };

    this.lmListEventService.listRowSelectedEvent(listRowSelectedEvent);
  }

  public setSelectedRows(rows: Array<number>, scrollIntoView?: boolean): void {    // [1, 3, 5 ]
    this.getSelectedRows();
  }

  activateRow(rowsToActivate: number, scrollIntoView?: boolean) {
    // if (this.lmBlockGridComponent.sohoBlockgrid) {
    //   this.lmBlockGridComponent.sohoBlockgrid.activateBlock(rowsToActivate);
    // }
  }

  activatedRow(): number {
    return -1;
  }

  onItemActivated(lmBlockItemEvent: LMBlockGridItemEvent) {
    // Open record if selection is disabled
    // disable select in webui
    if (this?.listSpec?.busListSpec?.selection === 'disabled') {
      this.selectedRows = [];
      this.onRowClicked(lmBlockItemEvent);
    } else {
      this.lmBlockItems.forEach((blockItem) => {
        if (blockItem.data.dvIndex === lmBlockItemEvent.row) {
          blockItem.check();
        } else {
          blockItem.unCheck();
        }
      });

      this.selectedRows = [].concat({
        idx: lmBlockItemEvent.row,
        data: this.blockGridOptions.data[lmBlockItemEvent.row],
        elem: null
      });

      const listRowSelectedEvent: LMListRowSelectedEvent = {
        event: lmBlockItemEvent.originalEvent,
        rows: this.selectedRows
      };

      this.lmListEventService.listRowSelectedEvent(listRowSelectedEvent);
    }
  }

  onSelected(lmBlockItemEvent: LMBlockGridItemEvent): void {
    if (this.selectionMode === 'single') {
      this.selectedRows = [];
    }

    // Open record if selection is disabled
    // And horizontal paging is true
    if (!this.selectionMode && this.lmListSpecUtilService.allowHorizontalPaging(this.listSpec)) {
      this.onDoubleClick(lmBlockItemEvent);
      return;
    }

    this.selectedItem = lmBlockItemEvent.item;
    const rowIsSelected = this.selectedRows.filter((row) => row.idx === lmBlockItemEvent.row).length > 0;

    if (!rowIsSelected) {
      this.blockGridOptions.data[lmBlockItemEvent.row].selected = true;
      this.selectedRows.push({
        idx: lmBlockItemEvent.row,
        data: this.blockGridOptions.data[lmBlockItemEvent.row],
        elem: null
      });
    }

    const listRowSelectedEvent: LMListRowSelectedEvent = {
      event: lmBlockItemEvent.originalEvent,
      rows: this.selectedRows
    };

    this.lmListEventService.listRowSelectedEvent(listRowSelectedEvent);
  }

  onUnSelected(blockGridItemEvent: LMBlockGridItemEvent) {
    this.selectedRows = this.selectedRows.filter((selectedRow) => selectedRow.idx !== blockGridItemEvent.row);
    this.blockGridOptions.data[blockGridItemEvent.row].selected = false;

    const listRowSelectedEvent: LMListRowSelectedEvent = {
      event: blockGridItemEvent.originalEvent,
      rows: this.selectedRows
    };

    this.lmListEventService.listRowSelectedEvent(listRowSelectedEvent);
  }

  onRowClicked(lmBlockItemEvent: LMBlockGridItemEvent): void {
    const listRowClickedEvent: LMListRowClickedEvent = {
      row: lmBlockItemEvent.row,
      cell: lmBlockItemEvent.cell,
      item: lmBlockItemEvent.item,
      originalEvent: lmBlockItemEvent.originalEvent
    };
    this.lmListEventService.listRowClickedEvent(listRowClickedEvent);
  }

  onDoubleClick(lmBlockItemEvent: LMBlockGridItemEvent): void {
    const listRowClickedEvent: LMListRowClickedEvent = {
      row: lmBlockItemEvent.row,
      cell: lmBlockItemEvent.cell,
      item: lmBlockItemEvent.item,
      originalEvent: lmBlockItemEvent.originalEvent
    };
    this.lmListEventService.listRowDoubleClickedEvent(listRowClickedEvent);
  }

  onContextMenu(lmBlockItemEvent: LMBlockGridItemEvent): void {
    this.selectedItem = lmBlockItemEvent.item;
    let rowIsAlreadySelected: boolean;

    this.selectedRows.forEach((selectedRow) => {
      if (selectedRow.idx === lmBlockItemEvent.row) {
        rowIsAlreadySelected = true;
      }
    });

    // check if row clicked on was already selected
    // if not, then unselect all rows and set to new selection
    if (!rowIsAlreadySelected) {
      this.unSelectAllRows();

      // updates checkbox state in lm-block-item template
      this.lmBlockItems.forEach((blockItem) => {
        if (blockItem.data.dvIndex === lmBlockItemEvent.row) {
          blockItem.check();
        }
      });

      this.setSelectedRows([ lmBlockItemEvent.row ]);

      this.selectedRows = [].concat({
        idx: lmBlockItemEvent.row,
        data: this.blockGridOptions.data[lmBlockItemEvent.row],
        elem: null
      });
    }

    this.onSelected(lmBlockItemEvent);
    this.lmListEventService.contextMenuEvent(lmBlockItemEvent);
  }

  onToolbarButtonEvent(event: LMToolbarButtonEvent) {
    this.lmListEventService.toolbarButtonEvent(event);
  }

  public initialize() {
    this.pagerIdAttributes = [ { name: 'id', value: this.listSpec.busListSpec.id + '_pager' } ];
    this.pageSizes = this.lmListUtilService.getPageSizesArray(this.listSpec);
    this.pageSize = this.lmListSpecUtilService.allowHorizontalPaging(this.listSpec) ? 20 : this.lmListUtilService.getPageSize(this.listSpec, this.listPersonalization);
    this.showAsCard = (!this.listSpec.showAsMingleWidget && !this.lmListSpecUtilService.isDashBoardContent(this.listSpec));
    this.selectionMode = this.lmListSpecUtilService.getSelectionMode(this.listSpec) as SohoListViewSelectable;   // check for has dependents
    this.showAsMingleWidget = this.listSpec.showAsMingleWidget;
  }

  protected build() {
    this.initialPage();
    this.initialBuildComplete = true;
  }

  protected filterCriteriaChanged() {
    if (this.filterCriteria && this.initialBuildComplete) {
      this.filterPage();
    } else if (this.initialBuildComplete && this.filterCriteria === null) {
      // a search filter has been cleared
      this.initialPage(true);
    }
  }

  public filterPage(): void {
    // Return if no filterCriteria is set then return
    if (!this.filterCriteria) {
      return;
    }

    if (this.listSpec.dependentList && !this._fromDataView) {
      this.reset();
      return;
    }

    this.handleCancelableRequest();
    this.cancelOutstandingPagingRequest();
    this.lmBusyIndicatorService.showBusyIndicator(this.sohoBusyIndicator);

    const url = this.buildInitialPageUrl(true);
    const postContent = this.lmUrlUtilService.buildSelectDataViewPagingRequest(this.selectRequestData);

    this.lmLoggerService.time({ id: 'LMListBlockGridBase.filterPage() ' + this.instanceId });
    this.pagingSubscription = this.lmListGridPagingService.filterPage(url, this.filterCriteria, this.pageSize, postContent).subscribe(
      (listPagingResponse: LMListPagingResponse) => {

        if (this.handleCancelableResponse(listPagingResponse)) {
          return;
        }

        if (listPagingResponse.serverLog) {
          this.lmLoggerService.serverLog(listPagingResponse.serverLog);
        }

        this.lmLoggerService.timeEnd('LMListBlockGridBase.filterPage() ' + this.instanceId, [ this, listPagingResponse.dataViewSet ]);
        this.lmBusyIndicatorService.hideBusyIndicator(this.sohoBusyIndicator);

        this.setEmptyMessage();

        if (listPagingResponse.status === 'COMPLETED') {
          this.postLoad(listPagingResponse);
          this.listState.filterCriteria = this.filterCriteria;

        } else {
          this.handleError(listPagingResponse.details, listPagingResponse.statusText);
        }
      },
      error => {
        this.lmLoggerService.timeEnd('LMListBlockGridBase.filterPage() ' + this.instanceId, [ this ]);
        this.handleError(error.stack, error.message);
        return;
      });
  }

  /**
   * todo: ppatton implement me! See lm-list-view.component for initial implementation
   */
  protected filterListCriteriaChanged() {}

  protected reportAsOfDateChanged(): void {
    //noop
  }

  private postLoad(listPagingResponse: LMListPagingResponse) {
    // set dataViewSet
    this.dataViewSet = listPagingResponse.dataViewSet;
    this.savedSearches = listPagingResponse.savedSearchDataViewSet;

    this.recordCount = listPagingResponse.dataViewSet.data.length;
    this.showEmptyMessage = this.recordCount === 0;
    // set pager button state
    this.hasNextPage = this.dataViewSet.pagingInfo.hasNext;
    this.hasPreviousPage = this.dataViewSet.pagingInfo.hasPrevious;
    this.hasFirstPage = !this.dataViewSet.pagingInfo.firstDisabled && this.dataViewSet.pagingInfo.hasPrevious;
    this.hasLastPage = !this.dataViewSet.pagingInfo.lastDisabled && this.dataViewSet.pagingInfo.hasNext;
    this.showPageSizeSelector = this.lmListUtilService.showPageSizeChooser(this.listSpec);

    // check column visibility
    this.checkColumnVisibility();

    // build the image list
    this.buildImageListData();
    if( listPagingResponse.pageOp != 'load' ){
      this.lmListEventService.dataLoadedEvent(listPagingResponse);
    }
  }

  private checkColumnVisibility() {
    if (this?.listSpec?.busListSpec?.sectionedCard?.sections) {
      const sections = this.listSpec.busListSpec.sectionedCard.sections.map((section: LMSectionedCardSection) => {
        if (section[Object.keys(section)[0]]?.fields) {
          const fields = [];
          section[Object.keys(section)[0]].fields.forEach((field: LMListDisplayField) => {
            if (field.columnVisibility) {
              const dataView = this.dataViewSet.header;
              if (this.lmConditionUtilService.isValid(field.columnVisibility, dataView)) {
                fields.push(field);
              }
            } else {
              fields.push(field);
            }
          });

          section[Object.keys(section)[0]].fields = fields;
        }

        return section;
      });

      this.listSpec.busListSpec.sectionedCard.sections = sections;
    }
  }

  private buildImageListData() {
    const options: BlockGridOptions = { data: Array<BlockGridData>() };

    for (let i = 0; i < this.dataViewSet.data.length; i++) {
      const dataView: LMDataView = this.dataViewSet.data[i];
      const foregroundText = this.getForegroundText(dataView);
      const blockGridData: BlockGridData = {
        imageUrl: dataView.representativeImageUrl,
        itemText: dataView.representativeText,
        dvIndex: i,
        detailFields: this._listSpec.busListSpec.detailFields,
        dataView,
        foregroundText,
        bgClass: this.lmThemeService.getUniqueBackgroundColorClass(foregroundText)
      };

      // Update selection for lm-block-item
      this.selectedRows.forEach((lmListRowData: LMListRowData) => {
        if (lmListRowData.data.dvIndex === blockGridData.dvIndex) {
          blockGridData.selected = true;
        }
      });

      options.data.push(blockGridData);
    }

    this.blockGridOptions = options;
  }

  private getForegroundText(dataView: LMDataView): string {
    if (this.listSpec
      && this.listSpec.busClassSpec
      && this.listSpec.busClassSpec.representativeImage
      && this.listSpec.busClassSpec.representativeImage.missingImageInfo
    ) {
      if (dataView.missingImage && this.listSpec.busClassSpec.representativeImage.missingImageInfo.foregroundText) {
        const msgRule: LMMessageRule = this.listSpec.busClassSpec.representativeImage.missingImageInfo.foregroundText;
        return this.lmFormatUtilService.formatMessageRule(msgRule, dataView);
      } else {
        return null;
      }
    } else {
      return null;
    }
  }

  private buildInitialPageUrl(forceUrlRebuild = false): string {

    if (this.listSpec.dependentList) {
      if (this.listState
        && this.listState.currentPageUrl
        && !forceUrlRebuild
        && !this.dependencyChanged()
        && this.lmUrlUtilService.allCsksMatchContext(this.listState.currentPageUrl)
      ) {
        // ------------------------------------------------------------------
        // if the fromDataView hasn't changed and there is a currentPageUrl
        // then load that to keep our position in the dependent list.
        // ------------------------------------------------------------------
        return this.listState.currentPageUrl;
      }

      // ------------------------------------------------------------------
      // else resolve template against the fromDataView's resourceId and
      // it's filterListFilterCriteria if this list has a FilterList
      // dependency
      // ------------------------------------------------------------------
      const compiledUrlTemplate = urltemplate.parse(this.listUrlInfo.initialDataUrl);
      let compiledUrl = compiledUrlTemplate.expand({ resourceId: this.fromDataView.resourceId });
      const delimiter: string = compiledUrl.indexOf('?') === -1 ? '?' : '&';

      if (this.hasFilterList && this.filterListFilterCriteria && this.filterListFilterCriteria) {
        // ------------------------------------------------------------------
        // apply any filterListFilterCriteria to the initial load.
        // ------------------------------------------------------------------    // set filter criteria on the url params
        const filterParamString = this.lmUrlUtilService.filterFieldCriteriaToUrlParams(this.filterListFilterCriteria, 'flc');
        if (filterParamString) {
          compiledUrl += delimiter + filterParamString;
        }
      }

      return compiledUrl;
    }

    if (this.listState
      && this.listState.currentPageUrl
      && !forceUrlRebuild
      && this.lmUrlUtilService.allCsksMatchContext(this.listState.currentPageUrl)
      && this.lmUrlUtilService.hasSameParamValue(this.listState.currentPageUrl, this.listUrlInfo.initialDataUrl, 'filter')
    ) {
      let currentUrl = this.listState.currentPageUrl;
      if (this.selectRequestData?.onSearchForm) {
        currentUrl = this.lmUrlUtilService.addParamToUrl(currentUrl, 'searchform', 'true');
      }
      return currentUrl;
    }

    let url = this.listUrlInfo.initialDataUrl;
    if (this.selectRequestData?.onSearchForm) {
      url = this.lmUrlUtilService.addParamToUrl(url, 'searchform', 'true');
    }

    return url;
  }

  private dependencyChanged() {
    if (!this.listSpec.dependentList || !this.listState) {
      // --------------------------------------------------------------
      // Must be a dependent list and have listState to check against
      // for a dependency to have changed. If not return false.
      // --------------------------------------------------------------
      return false;
    }

    // --------------------------------------------------------------
    // If the from dataView resourceId change then the dependency
    // has changed. return true.
    // --------------------------------------------------------------
    if (this.fromDataView.resourceId !== this.listState.fromResourceId) {
      return true;
    }

    if (this.hasFilterList) {
      if (!this.listState.filterListFilterCriteria || this.listState.filterListFilterCriteria.length === 0) {
        // filter list criteria was empty, now it's been set.
        return (this.filterListFilterCriteria && this.filterListFilterCriteria.length >= 1);
      }

      // --------------------------------------------------------------
      // If this list has a filterList then check to see if the
      // filterListFilterCriteria has changed. If so then return true.
      // --------------------------------------------------------------
      // todo ppatton - this is NOT robust AT ALL. Need to handle checking multiple filterListCriteria elements.
      return this.filterListFilterCriteria[ 0 ].value !== this.listState.filterListFilterCriteria[ 0 ].value;
    }

    return false;
  }
}
